import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.scss";
import { FiArrowUpRight } from "react-icons/fi";
import PreviewReportWhiteIcon from "../../../icons/PreviewReportWhiteIcon";
import GreenTickOutlineIcon from "../../../icons/GreenTickOutlineIcon";
import SearchIcon from "../../../icons/SearchIcon";
import { Button } from "react-bootstrap";
import PdfReportIcon from "../../../icons/PdfReportIcon";
import ExcelReportIcon from "../../../icons/ExcelReportIcon";
import CsvReportIcon from "../../../icons/CsvReportIcon";
import TxfReportIcon from "../../../icons/TxfReportIcon";
import { YEARS_LIST } from "../../../constants/years";
import LocationIcon from "../../../icons/LocationIcon";
import { getPropertyListForFilter } from "../../../core/data/redux/property/_propertyAction";
import { useDispatch } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { generateReport } from "../../../core/data/redux/report/_reportAction";
import { toast } from "react-toastify";
import DocReportIcon from "../../../icons/DocReportIcon";

const exportOptions = [
  {
    id: "pdf",
    label: "PDF (IRS-Ready)",
    description: "Professional formatted report for IRS submission",
    icon: <PdfReportIcon />,
  },
  {
    id: "docx",
    label: "Docx (Detailed)",
    description: "Spreadsheet with formulas and detailed calculations",
    icon: <DocReportIcon />,
  },
  {
    id: "xlsx",
    label: "Xlsx (Detailed)",
    description: "Spreadsheet with formulas and detailed calculations",
    icon: <ExcelReportIcon />,
  },
  // {
  //   id: "csv",
  //   label: "CSV (Raw Data)",
  //   description: "Raw data export for further analysis",
  //   icon: <CsvReportIcon />,
  // },
  // {
  //   id: "txf",
  //   label: "Tax Software (.txf)",
  //   description: "Import directly into tax preparation software",
  //   icon: <TxfReportIcon />,
  // },
];

const SpouseReports: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedExportFormat, setSelectedExportFormat] = useState<
    string | null
  >(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [scope, setScope] = useState<string | null>(null);
  const [propertyType, setPropertyType] = useState<string | null>(null);
  const [realEstateActivity, setRealEstateActivity] = useState<string | null>(
    null
  );
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";

  const [errors, setErrors] = useState({
    year: "",
    exportFormat: "",
    scope: "",
    propertyType: "",
    realEstateActivity: "",
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch properties on component mount
  useEffect(() => {
    getAllPropertyList();
  }, [dispatch]);

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({}));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Generate & Download report handler
  const handleGenerateReport = async () => {
    const newErrors = {
      year: selectedYear ? "" : "Please select a reporting year",
      exportFormat: selectedExportFormat
        ? ""
        : "Please select an export format",
      scope: scope ? "" : "Please select whose’s time",
      propertyType: propertyType ? "" : "Please select property type",
      realEstateActivity: realEstateActivity
        ? ""
        : "Please select activity type",
    };
    setErrors(newErrors);

    if (newErrors.year || newErrors.exportFormat) {
      return;
    }

    setIsGenerating(true);
    try {
      // Get local timezone string
      const payload = {
        year: selectedYear,
        scope: "spouse",
        property_type: propertyType || "",
        property: selectedProperty || "",
        real_estate_activity: realEstateActivity || "",
        file_type: selectedExportFormat,
        category: "real_estate",
        timezone,
      };

      const response = await dispatch(generateReport(payload));

      if (response?.payload?.success) {
        const fileUrl = response.payload.data?.file;
        if (fileUrl) {
          // Fetch file as Blob to force download
          const fileResponse = await fetch(fileUrl);
          const blob = await fileResponse.blob();
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `report_${selectedYear}.${selectedExportFormat}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          window.URL.revokeObjectURL(blobUrl); // clean up
          toast.success("Report downloaded successfully.");
        } else {
          toast.success("Report generated successfully.");
        }
      } else {
        toast.error("Failed to generate report. Please try again.");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("An error occurred while generating the report.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePreviewReport = async () => {
    const newErrors = {
      year: selectedYear ? "" : "Please select a reporting year",
      exportFormat: selectedExportFormat
        ? ""
        : "Please select an export format",
      scope: scope ? "" : "Please select whose’s time",
      propertyType: propertyType ? "" : "Please select property type",
      realEstateActivity: realEstateActivity
        ? ""
        : "Please select activity type",
    };
    setErrors(newErrors);

    if (newErrors.year || newErrors.exportFormat) {
      return;
    }

    const payload = {
      year: selectedYear,
      scope: "spouse",
      property_type: propertyType || "",
      property: selectedProperty || "",
      real_estate_activity: realEstateActivity || "",
      file_type: selectedExportFormat,
      category: "real_estate",
      timezone
    };

    try {
      const response = await dispatch(generateReport(payload));
      if (response?.payload?.success) {
        const fileUrl = response.payload.data?.file;
        if (fileUrl) {
          window.open(fileUrl, "_blank"); // open PDF preview in new tab
        } else {
          toast.error("No PDF file available for preview.");
        }
      } else {
        toast.error("Failed to generate preview report. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while generating the preview.");
    }
  };

  const handleReportEmail = async () => {
    const newErrors = {
      year: selectedYear ? "" : "Please select a reporting year",
      exportFormat: selectedExportFormat
        ? ""
        : "Please select an export format",
      scope: scope ? "" : "Please select whose’s time",
      propertyType: propertyType ? "" : "Please select property type",
      realEstateActivity: realEstateActivity
        ? ""
        : "Please select activity type",
    };
    setErrors(newErrors);

    if (newErrors.year || newErrors.exportFormat) {
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        year: selectedYear,
        scope: "spouse",
        property_type: propertyType || "",
        property: selectedProperty || "",
        real_estate_activity: realEstateActivity || "",
        file_type: selectedExportFormat,
        category: "real_estate",
        is_email: true,
        timezone
      };

      const response = await dispatch(generateReport(payload));

      // if (response?.payload?.success) {
      //   const fileUrl = response.payload.data?.file;
      //   if (fileUrl) {
      //     window.open(fileUrl, "_blank");
      //   } else {
      toast.success("Report generated successfully.");
      // }
      // } else {
      //   toast.error("Failed to generate report. Please try again.");
      // }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("An error occurred while sending report to email.");
    } finally {
      setIsGenerating(false);
    }
  };

  const reportData = [];

  return (
    <div className="layout ">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings report-page-section">
          <div className=" rounded report-page-inner">
            <div className="add-properties-heading d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
              <h2 className="mb-0">Reports</h2>

              <Button
                className="generate-report-button"
                onClick={() => navigate(routes.spouseGeneratedReports)}
              >
                Generated Reports <FiArrowUpRight />
              </Button>
            </div>

            {/*  Choose Reporting Year */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                <label className="form-label">Choose Reporting Year</label>

                <div className="dropdown" id="reporting-year-dropdown">
                  <button
                    className="form-select d-flex justify-content-between align-items-center bg-light form-control commonInput"
                    onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                    type="button"
                  >
                    {selectedYear || "Choose Reporting Year"}
                    <i
                      className={`bi bi-chevron-${isYearDropdownOpen ? "up" : "down"
                        }`}
                    />
                  </button>

                  {isYearDropdownOpen && (
                    <div
                      className="dropdown-menu show w-100 shadow-sm mt-1 bg-light p-0"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      {YEARS_LIST?.map((year) => (
                        <div
                          key={year}
                          className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom"
                          onClick={() => {
                            setSelectedYear(year.toString());
                            setIsYearDropdownOpen(false);
                            setErrors((prev) => ({ ...prev, year: "" }));
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <span>{year}</span>
                          <input
                            type="radio"
                            name="reportingYear"
                            className="form-check-input"
                            checked={selectedYear === year.toString()}
                            readOnly
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.year && (
                    <div className="text-danger mt-1 small">{errors.year}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Configure Your Report */}
            <div className="row  mb-3">
              <div className="mb-0 mt-4">
                <h3 className="mb-3">Configure Your Report</h3>
              </div>

              <div className="col-12 col-sm-12 col-md-12 col-lg-6 mb-3">
                <label className="form-label">Whose’s Time</label>
                <input
                  type="text"
                  className="form-control commonInput bg-light"
                  value="Spouse"
                  disabled
                />
              </div>

              <div className="col-12 col-sm-12 col-md-12 col-lg-6 mb-3">
                <label className="form-label">Property Type</label>
                <select
                  className="form-select form-control commonInput bg-light"
                  value={propertyType || ""}
                  onChange={(e) => {
                    const value = e.target.value || null;
                    setPropertyType(value);
                    if (errors.propertyType && value) {
                      setErrors((prev) => ({ ...prev, propertyType: "" }));
                    }
                  }}
                >
                  <option value="" disabled selected hidden>
                    Select Property Type
                  </option>
                  <option value="all">All Property Type </option>
                  <option value="ltr">LTR </option>
                  <option value="str">STR</option>
                </select>
                {errors.propertyType && (
                  <div className="text-danger mt-1 small">
                    {errors.propertyType}
                  </div>
                )}
              </div>

              <div
                className="col-12 col-sm-12 col-md-12 col-lg-6  mb-3"
                ref={dropdownRef}
              >
                <label className="form-label">Property (Option)</label>
                <div className="dropdown ">
                  <button
                    className="form-select d-flex justify-content-between align-items-center bg-light form-control commonInput "
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {selectedPropertyName || "Select Property (Option)"}
                    <i
                      className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div
                      className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                      style={{ maxHeight: "initial" }}
                    >
                      <div className="input-group mb-3 commonInput bg-light bg-white">
                        <span className="input-group-text bg-white border-end-0 ">
                          <SearchIcon />
                        </span>
                        <input
                          type="text"
                          placeholder="Search properties by name or address"
                          className="name form-control border-start-0"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>

                      {filteredProperties.length > 0 ? (
                        filteredProperties.map((property, index) => (
                          <div
                            key={index}
                            className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput  bg-light"
                            onClick={() => {
                              setSelectedProperty(property?.uuid);
                              setSelectedPropertyName(property?.name);
                              setIsDropdownOpen(false);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <label className="form-check-label ms-2 d-flex flex-column gap-3">
                              <strong>{property.name}</strong>

                              <small className="text-muted d-flex align-items-end">
                                <LocationIcon />
                                {property.address}, {property?.city},{" "}
                                {property?.state} {property?.zipcode}
                              </small>
                            </label>
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="propertyRadio"
                              checked={selectedPropertyName === property.name}
                              readOnly
                            />
                          </div>
                        ))
                      ) : (
                        <div className="text-muted text-center">
                          No results found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-sm-12 col-md-12 col-lg-6 mb-3">
                <label className="form-label">Activity Type</label>
                <select
                  className="form-select form-control commonInput bg-light"
                  value={realEstateActivity || ""}
                  onChange={(e) => {
                    const value = e.target.value || null;
                    setRealEstateActivity(value);
                    if (errors.realEstateActivity && value) {
                      setErrors((prev) => ({
                        ...prev,
                        realEstateActivity: "",
                      }));
                    }
                  }}
                >
                  <option value="" disabled selected hidden>
                    Activity Type
                  </option>
                  <option value="all">All Activities</option>
                  <option value="material_participation">
                    Material Participation{" "}
                  </option>
                  <option value="general_activity">General </option>
                </select>
                {errors.realEstateActivity && (
                  <div className="text-danger mt-1 small">
                    {errors.realEstateActivity}
                  </div>
                )}
              </div>
            </div>

            {/* --- Choose Export Format ---  */}
            <div className="row">
              <div className="col-sm-12">
                <div className="bg-white">
                  <label className="export-format-label form-label">
                    Choose Export Format
                  </label>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <div className="bg-white chooseExpertFormat">
                  <div className="row align-items-stretch">
                    {exportOptions.map(({ id, label, description, icon }) => (
                      <div key={id} className="col-12 col-md-6 mb-4">
                        <div
                          className={`export-option-card h-100 ${selectedExportFormat === id ? "selected" : ""}`}
                          onClick={() => {
                            setSelectedExportFormat(id);
                            setErrors((prev) => ({
                              ...prev,
                              exportFormat: "",
                            }));
                          }}
                        >
                          <div className="export-option-content">
                            <div className="export-option-icon">{icon}</div>
                            <div>
                              <div className="export-option-label">{label}</div>
                              <div className="export-option-description">
                                {description}
                              </div>
                            </div>
                          </div>

                          <input
                            type="checkbox"
                            name="exportFormat"
                            checked={selectedExportFormat === id}
                            onChange={() => {
                              setSelectedExportFormat(id);
                              setErrors((prev) => ({
                                ...prev,
                                exportFormat: "",
                              }));
                            }}
                            className="export-option-radio"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.exportFormat && (
                    <div className="text-danger mt-1 small">
                      {errors.exportFormat}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* --- Choose Export Format End --- */}

            {/* --- Choose Export Format 2 --- */}
            <div className="row mt-4 bg-white ">
              <label className="export-format-label form-label">
                Choose Export Format
              </label>
              <div className="delivery-method-section  p-3 form-control commonInput">
                <div className="d-flex gap-3 flex-wrap">
                  <button
                    className="btn  d-flex align-items-center gap-2 px-3 py-2 text-white "
                    onClick={handlePreviewReport}
                    disabled={selectedExportFormat !== "pdf"} // enable only for PDF
                  >
                    <PreviewReportWhiteIcon />
                    Preview Report
                  </button>

                  <button
                    className="btn btn-2  d-flex align-items-center gap-2 px-3 py-2 text-white"
                    onClick={handleGenerateReport}
                  >
                    <PreviewReportWhiteIcon />
                    Generate & Download
                  </button>

                  <button
                    className="btn btn-3  d-flex align-items-center gap-2 px-3 py-2 text-white"
                    onClick={handleReportEmail}
                  >
                    <PreviewReportWhiteIcon />
                    Email to Tax Professional
                  </button>
                </div>

                <div className="alert bg-white mt-3 d-flex align-items-top gap-3 form-control commonInput p-4">
                  <span
                    style={{
                      minWidth: "25px",
                      minHeight: "25px",
                      display: "inline-block",
                    }}
                  >
                    <GreenTickOutlineIcon />
                  </span>
                  <span>
                    <p className="mb-2">
                      <strong>IRS-Compliant Documentation Ready</strong>
                    </p>
                    <p>
                      Your report will include digital signatures, audit trails,
                      and all required documentation for real estate
                      professional status.
                    </p>
                  </span>
                </div>
              </div>
            </div>
            {/* --- Choose Export Format End 2 --- */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SpouseReports;
